import { css, StyleSheet } from 'aphrodite';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { BlogFont } from '../../../extViewmodels';
import { parseGoogleFontInput } from '../../../utils/googleFontUtils';
import { Collapsible } from '../../../web/components/Collapsible';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../web/components-v2/Dropdown';
import { background, black, charmGray, shuffleGray } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { InfoPopover } from '../InfoPopover/InfoPopover';
import { LabeledInput } from '../LabeledInput/LabeledInput';
import { Column, Row } from '../Layout';

type FontSourceType = 'google-font' | 'web-font';

interface FontSourceOption {
	source: FontSourceType;
	id: string;
	text: string;
}

interface WebFontOption {
	fontName: string;
	id: string;
	text: string;
}

interface FontSelectProps {
	onChange: (selectedFont: BlogFont) => void;
	errors?: { fontName?: FieldError; fontUri?: FieldError };
	initialValue: BlogFont;
	value: BlogFont;
	labelAccessory?: ReactNode;
}

const FONT_SOURCE_OPTIONS: FontSourceOption[] = [
	{
		source: 'google-font',
		id: 'google-font-option',
		text: 'Google Font',
	},
	{
		source: 'web-font',
		id: 'web-font-option',
		text: 'Web Default Font',
	},
];

const WEB_FONT_OPTIONS: WebFontOption[] = [
	{ fontName: 'Arial', id: 'arial-font-option', text: 'Arial' },
	{ fontName: 'Verdana', id: 'verdana-font-option', text: 'Verdana' },
	{ fontName: 'Tahoma', id: 'tahoma-font-option', text: 'Tahoma' },
	{ fontName: 'Trebuchet MS', id: 'trebuchet-ms-font-option', text: 'Trebuchet MS' },
	{ fontName: 'Times New Roman', id: 'tnr-font-option', text: 'Times New Roman' },
	{ fontName: 'Georgia', id: 'georgia-font-option', text: 'Georgia' },
	{ fontName: 'Garamond', id: 'garamond-font-option', text: 'Garamond' },
	{ fontName: 'Courier New', id: 'courier-new-font-option', text: 'Courier New' },
	{ fontName: 'Brush Script MT', id: 'brush-script-font-option', text: 'Brush Script MT' },
];

export const FontSelect: FC<FontSelectProps> = ({ errors, initialValue, onChange, value, labelAccessory }) => {
	const [fontSource, setFontSource] = useState<FontSourceOption | null>(null);
	const [selectedWebFont, setSelectedWebFont] = useState<WebFontOption | null>(null);

	const handleFontSourceSelect = useCallback((option: FontSourceOption) => {
		setFontSource(option);
	}, []);

	const handleWebFontSelect = useCallback(
		(option: WebFontOption) => {
			setSelectedWebFont(option);
			onChange({ fontUri: '', fontName: option.fontName });
		},
		[onChange]
	);

	const handleFontUriChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const inputValue = e.target.value;
			const { fontUri, fontName } = parseGoogleFontInput(inputValue);
			onChange({ fontName, fontUri });
		},
		[onChange]
	);

	useEffect(() => {
		if (initialValue?.fontUri && fontSource?.source !== 'google-font') {
			setFontSource(FONT_SOURCE_OPTIONS[0]);
		} else if (initialValue?.fontName && !initialValue?.fontUri && fontSource?.source !== 'web-font') {
			const option = WEB_FONT_OPTIONS.find(val => val.fontName === initialValue.fontName);

			setFontSource(FONT_SOURCE_OPTIONS[1]);
			setSelectedWebFont(option || null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValue]);

	return (
		<>
			<Column>
				<Row className={css(fontSelectStyleSheet.labelRow)}>
					<label className={css(fontSelectStyleSheet.label)}>Font Source</label>
					{labelAccessory}
				</Row>
				<Dropdown>
					<DropdownSelectTrigger styles={[fontSelectStyleSheet.container]}>
						{fontSource?.text || 'Select font source'}
					</DropdownSelectTrigger>
					<DropdownContent align='start' styles={[fontSelectStyleSheet.dropdownContent]}>
						{FONT_SOURCE_OPTIONS.map(option => (
							<DropdownItem
								key={option.id}
								onClick={() => handleFontSourceSelect(option)}
								styles={[fontSource?.id === option.id && bs.bgGray200]}
							>
								{option.text}
							</DropdownItem>
						))}
					</DropdownContent>
				</Dropdown>
			</Column>
			<Column className={css(fontSource ? null : fontSelectStyleSheet.collapsible)}>
				<Collapsible isCollapsed={!(fontSource?.source === 'web-font')}>
					<Column>
						<Row className={css(fontSelectStyleSheet.labelRow)}>
							<label className={css(fontSelectStyleSheet.label)}>Web Default Font</label>
						</Row>
						<Dropdown>
							<DropdownSelectTrigger styles={[fontSelectStyleSheet.container]}>
								{selectedWebFont?.text || 'Select font'}
							</DropdownSelectTrigger>
							<DropdownContent align='start' styles={[fontSelectStyleSheet.dropdownContent]}>
								{WEB_FONT_OPTIONS.map(option => (
									<DropdownItem
										key={option.id}
										onClick={() => handleWebFontSelect(option)}
										styles={[selectedWebFont?.id === option.id && bs.bgGray200]}
									>
										{option.text}
									</DropdownItem>
								))}
							</DropdownContent>
						</Dropdown>
					</Column>
				</Collapsible>
				<Collapsible isCollapsed={!(fontSource?.source === 'google-font')}>
					<LabeledInput
						error={errors?.fontUri}
						label='Google Font URL'
						labelAccessory={
							<InfoPopover message='To use a Google Font, visit https://fonts.google.com/ and select a font. Copy the URL from the "Embed" tab and paste it here. You can paste either the URL directly or the full <link> tag.' />
						}
						onChange={handleFontUriChange}
						value={value?.fontUri ?? ''}
					/>
				</Collapsible>
			</Column>
		</>
	);
};

export const fontSelectStyleSheet = StyleSheet.create({
	collapsible: {
		display: 'none',
	},
	container: {
		backgroundColor: background,
		border: `1px solid ${shuffleGray}`,
		borderRadius: 8,
		color: black,
		height: 44,
		width: '100%',
		minWidth: '100%',
	},
	dropdownContent: {
		minWidth: 200,
	},
	info: {
		fontSize: 12,
		color: charmGray,
		textAlign: 'right',
	},
	label: {
		fontSize: 14,
	},
	labelRow: {
		gap: 4,
		paddingBottom: 8,
	},
	spacer: {
		paddingBottom: 24,
	},
});
